import React, { FC, useEffect, useReducer } from 'react';

import { SortOrder } from '@ariksa/notification';
import {
  FilterEntityType,
  GroupTicketResponse,
} from '@ariksa/notification/api';
import { Box, HStack, Stack } from '@chakra-ui/react';
import { isEqual } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';

import { isAlertVaultEnabled } from 'api/alert-vault/client';
import {
  cloudProviderOptionsWithAll,
  StackedHeader,
} from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { Sorted } from 'components/DataDisplay/NewTable/types';
import { severityOptionsWithAll } from 'components/DataDisplay/Utils/renderSeverity';
import { IconTypes } from 'components/Icons';
import { PageInfo } from 'components/Navigation';
import { Drawer } from 'components/Overlay';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { FindingTicketStatus } from 'containers/Findings/Tickets/Components/GroupTicketDetails/FindingTicketStatus';
import { useTicketsColumns } from 'containers/Findings/Tickets/Components/hooks/useTicketsColumns';
import { TicketFilters } from 'containers/Findings/Tickets/Components/TicketFilters';
import { overDueFilterOptions } from 'containers/Findings/Tickets/Components/utils';
import { selectTickets } from 'containers/Findings/Tickets/selectors';
import { actions } from 'containers/Findings/Tickets/slice';
import { selectSharedState } from 'containers/SharedState/selectors';

interface Props {
  isOpen: boolean;
  onClose();
  ticket: GroupTicketResponse;
  handleRowClick(row);
}

export const GroupTicketDetails: FC<Props> = props => {
  const { isOpen, onClose, ticket, handleRowClick } = props;
  const dispatch = useDispatch();
  const { childTickets, issueAssignees } = useSelector(selectTickets);
  const { environmentId, accountId } = useAccessBoundary();
  const { issueColumns } = useTicketsColumns();
  const { alertResourceTypes } = useSelector(selectSharedState);
  const { getAlertResourceTypeOptionsWithAll } = useResourceType();
  const [sortBy, setSortBy] = React.useState<Sorted>({
    sortOrder: 'desc',
    sortField: 'resource_id',
  });
  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      severity: severityOptionsWithAll[0],
      status: { label: 'State', value: undefined },
      resourceType: {},
      provider: cloudProviderOptionsWithAll[0],
      resourceTypeOptions: [],
      isOverDueDate: overDueFilterOptions[1],
      assigneeOptions: [],
      assignee: { label: 'Owner', value: undefined },
    },
  );

  useEffect(() => {
    dispatch(
      actions.getChildTickets({
        q: {
          environmentId: environmentId!,
          accountIds: !!accountId ? [accountId] : undefined,
          state: state?.status?.value,
          severity: state?.severity?.value,
          resourceType: state?.resourceType?.value,
          overDueDate: state?.isOverDueDate?.value,
          cloud: state?.provider?.value,
          sortField: sortBy.sortField,
          sortOrder: sortBy.sortOrder?.toUpperCase() as SortOrder,
          pageSize: childTickets.page.info.page_size,
          pageNumber: childTickets.page.info.page_number,
          groupId: ticket?.group_id,
          assigneeId: state?.assignee?.value,
        },
      }),
    );
  }, [
    dispatch,
    environmentId,
    accountId,
    childTickets.page.info,
    state.status,
    state.severity,
    state.resourceType,
    state.isOverDueDate,
    state.provider,
    sortBy,
    ticket,
    state?.assignee,
  ]);

  useEffect(() => {
    dispatch(
      actions.getIssueAssignees({
        q: {
          environmentId: environmentId!,
          accountIds: accountId ? [accountId] : undefined,
          filterEntity: FilterEntityType.IssueTicket,
        },
      }),
    );
  }, [dispatch, environmentId, accountId]);

  //set resource type options
  useEffect(() => {
    const options = getAlertResourceTypeOptionsWithAll(alertResourceTypes.data);
    updateState({
      resourceTypeOptions: options,
      resourceType: options[0],
    });
  }, [alertResourceTypes.data, getAlertResourceTypeOptionsWithAll]);

  //set assignee options
  useEffect(() => {
    const options = [
      { label: 'Owner', value: undefined },
      ...map(issueAssignees.data, o => ({
        label: o.assignee_name,
        value: o.assignee_id,
      })),
    ];
    updateState({ assigneeOptions: options });
  }, [issueAssignees.data]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      header={
        <StackedHeader
          type={IconTypes.Ticket}
          upper={ticket.finding}
          lower={ticket.ticket_id}
        />
      }
      body={
        <Stack h="full" spacing={6}>
          <FindingTicketStatus ticket={ticket} />
          <BorderedBox h="full">
            <Stack h="full" spacing={5}>
              <HStack>
                <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
                <TicketFilters
                  {...state}
                  updateValue={(field, value) =>
                    updateState({ [field]: value })
                  }
                  isDisabled={childTickets.isLoading}
                  isLoadingAssignees={issueAssignees.isLoading}
                />
              </HStack>
              <Box flex={1} w="full" h="full">
                <Table
                  data={childTickets.data}
                  columns={issueColumns}
                  isLoading={childTickets.isLoading}
                  cursor="pointer"
                  styles={{ header: { zIndex: 800 } }}
                  sortBy={sortBy}
                  onSort={sortInfo => {
                    if (!isEqual(sortBy, sortInfo)) {
                      setSortBy(sortInfo);
                    }
                  }}
                  onRowClick={row => {
                    handleRowClick(row);
                  }}
                  pagination={{
                    totalCount: childTickets.page.totalCount,
                    pageInfo: childTickets.page.info,
                    cursorBased: isAlertVaultEnabled(),
                    onChange(info: PageInfo) {
                      dispatch(actions.updateChildTicketPagination(info));
                    },
                  }}
                />
              </Box>
            </Stack>
          </BorderedBox>
        </Stack>
      }
      styles={{ drawer: { size: '1200px' } }}
    />
  );
};
